"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { usePreferences } from "@/stores/preferences";
import { mixAllowedForUnit, leadColor } from "@/lib/catalog/color-selection";
import type { Card } from "@/lib/catalog/grouping";
import { varietyKey } from "@/lib/availability/keys";
import type { PlainCatalogStrings } from "@/lib/i18n/catalog-strings";
import type { Locale } from "@/lib/i18n/getLocale";
import { GradientPlaceholder } from "./GradientPlaceholder";
import { QuickAddButton } from "./QuickAddButton";
import { ProductCardPricing } from "./ProductCardPricing";
import { FmBanner } from "./FmBanner";
import { HarvestArrivalRow } from "./HarvestArrivalRow";
import { SpecRow } from "./SpecRow";
import { ColorPicker, type ColorChoice } from "./ColorPicker";
import { QuickAddPopover } from "./QuickAddPopover";
import { useCatalogFeedback } from "@/stores/catalog-feedback";
import { publicFarmName } from "@/lib/seo/jsonld";
import { useLocaleHref } from "@/lib/i18n/use-locale-href";

export function ProductCard({
  card,
  strings,
  now,
  compact = false,
  index,
  frequentBadge,
  locale = "en",
  soldOutKeys,
}: {
  card: Card;
  strings: PlainCatalogStrings;
  now: Date;
  // #452: active sold-out keys. A whole-variety key greys the card + disables
  // the quick-add button; color/length scopes are handled inside the popover.
  soldOutKeys?: string[];
  // MKT-#93: locale for the own-production altitude badge (ES = "3.200m").
  locale?: Locale;
  // Step 6.7.3.1 / AC187: compact variant used inside the
  // MoreFromCategory carousel - lighter padding, smaller name, hides
  // the harvest/arrival meta row to keep the card height predictable
  // across narrower viewport widths.
  compact?: boolean;
  // Position in the grid (commit 23 LCP tuning): the first cards load
  // eagerly (index 0 with fetchpriority=high since it is the mobile LCP
  // element); everything else lazy-loads. Omitted (carousels) -> lazy.
  index?: number;
  // Step 9 commit 11 / AC507: dashboard catalog highlights varieties
  // the client orders frequently. When set, a small pill renders next
  // to the category line.
  frequentBadge?: string;
}) {
  const localeHref = useLocaleHref();
  const [activeIdx, setActiveIdx] = useState(0);
  const variety = card.variants[activeIdx];
  // Color picker state - only relevant when the active variant has
  // colorOptions. Defaults to the variety's primary colorTag (so headline
  // price matches what the catalog filter shows). MIX option records "mix".
  const hasColorPicker =
    Array.isArray(variety.colorOptions) && variety.colorOptions.length > 0;
  // #468 (Alexis): by-bunch default = LEAD color, never Mix. Mix is a by-box
  // option only, so this preview selector offers it only in Box mode.
  const [colorChoice, setColorChoice] = useState<ColorChoice>(
    hasColorPicker ? (leadColor(variety) as ColorChoice) : "",
  );
  const globalUnit = usePreferences((s) => s.unitPreference);
  const previewUnit: "bunch" | "box" =
    variety.purchaseUnit === "box" ? "box" : globalUnit;
  useEffect(() => {
    if (previewUnit === "bunch" && colorChoice === "mix") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setColorChoice(leadColor(variety) as ColorChoice);
    }
  }, [previewUnit, colorChoice, variety]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  // #468 catalog bar: an add feeds the ONE shared builder bar (see
  // stores/catalog-feedback + CartBuilderBar), replacing the per-card toast.
  const pushAdd = useCatalogFeedback((s) => s.pushAdd);
  // Step 6.6 simplification: cards only show length chips on rose category.
  // Other categories' lengths don't drive price or capacity and were
  // leaking onto specialty cards (e.g. asiatic-lily showed "50 60" chips).
  // Multi-length roses get the interactive picker via ProductCardPricing
  // already, so static chips are skipped there too. Single-length rose
  // (Blue Tinted) stays without chips.
  const showStaticLengthChips =
    variety.category === "rose" && variety.lengths.length === 0;
  const showSpecRow =
    variety.category === "rose" || variety.category === "garden-spray-rose";

  // #452: whole active variant sold out -> grey the card + block quick-add.
  // (Color/length scopes leave the card active; the popover greys them.)
  const keySet = useMemo(() => new Set(soldOutKeys ?? []), [soldOutKeys]);
  const varietySoldOut = keySet.has(varietyKey(variety.slug));

  function openPopover(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (varietySoldOut) return;
    setPopoverOpen(true);
  }

  return (
    <>
    <Link
      href={localeHref(`/catalog/${card.routeSlug}`)}
      data-testid="product-card"
      data-slug={variety.slug}
      data-route-slug={card.routeSlug}
      data-grouped={card.isGrouped || undefined}
      data-category={variety.category}
      data-color={variety.colorTag}
      data-lengths={variety.lengths.join(",")}
      data-farm={publicFarmName(variety.farm.name)}
      data-purchase-unit={variety.purchaseUnit}
      data-compatibility-group={variety.compatibilityGroup}
      data-sold-out={varietySoldOut ? "true" : undefined}
      className={
        "group relative block overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md" +
        (varietySoldOut ? " opacity-60" : "")
      }
    >
      <div className="relative aspect-[4/3] w-full">
        <GradientPlaceholder
          variety={variety}
          photoComingSoonLabel={strings.photoComingSoon}
          className="h-full w-full"
          imgLoading={index !== undefined && index < 3 ? "eager" : "lazy"}
          fetchPriority={index === 0 ? "high" : undefined}
        />
        <FmBanner farm={variety.farm} variant="card" label={strings.fmBannerLine} locale={locale} />
        <QuickAddButton
          ariaLabel={`Add ${card.name} to box`}
          onClick={openPopover}
          soldOut={varietySoldOut}
        />
      </div>
      <div className={compact ? "flex flex-col gap-1 p-2.5" : "flex flex-col gap-1.5 p-4"}>
        <h3
          data-testid="product-card-name"
          className={compact ? "text-sm font-semibold text-zinc-900 leading-tight" : "text-lg font-semibold text-zinc-900"}
        >
          {card.name}
        </h3>
        {!compact && (
          <HarvestArrivalRow
            harvestCycleDays={variety.harvestCycleDays}
            harvestedLabel={strings.harvestedLabel}
            arrivesLabel={strings.arrivesLabel}
            variant="card"
            now={now}
          />
        )}
        {!compact && showSpecRow && (
          <SpecRow
            headSizeCm={variety.headSizeCm}
            petalCount={variety.petalCount}
            stemsPerBunch={variety.stemsPerBunch}
            lengthSuffixCm={strings.lengthSuffixCm}
            petalsUnit={strings.specRowPetalsUnit}
            stemsUnit={strings.specRowStemsUnit}
          />
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <p
            data-testid="product-card-category"
            className="text-xs font-semibold uppercase tracking-wide text-brand-teal"
          >
            {strings.category[variety.category]}
          </p>
          {frequentBadge && (
            <span
              data-testid="product-card-frequent"
              className="inline-flex items-center rounded-full bg-brand-teal px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
            >
              {frequentBadge}
            </span>
          )}
        </div>
        {card.isGrouped && (
          <div
            data-testid="product-card-grade-picker"
            className="flex flex-wrap gap-1.5"
          >
            {card.variants.map((v, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={v.slug}
                  type="button"
                  data-testid={`product-card-grade-${v.grade?.toLowerCase()}`}
                  data-grade={v.grade}
                  aria-pressed={isActive}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveIdx(i);
                  }}
                  className={
                    isActive
                      ? "min-h-6 rounded-full bg-brand-teal px-2.5 py-0.5 text-xs font-semibold text-white"
                      : "min-h-6 rounded-full border border-brand-border px-2.5 py-0.5 text-xs text-brand-text-muted hover:border-brand-teal hover:text-brand-text"
                  }
                >
                  {v.grade}
                </button>
              );
            })}
          </div>
        )}
        {showStaticLengthChips && (
          <div className="flex flex-wrap gap-1.5">
            {variety.lengths.map((l) => (
              <span
                key={l}
                data-testid="product-card-length-chip"
                className="inline-flex items-center rounded-full border border-brand-teal/50 px-2 py-0.5 text-xs text-zinc-700"
              >
                {l === 80 ? strings.length80Label : l} {strings.lengthSuffixCm}
              </span>
            ))}
          </div>
        )}
        {hasColorPicker && (
          <ColorPicker
            options={variety.colorOptions ?? []}
            active={colorChoice}
            allowMix={mixAllowedForUnit(variety, previewUnit)}
            onChange={setColorChoice}
            variant="card"
            testIdPrefix="product-card-color"
          />
        )}
        <ProductCardPricing
          variety={variety}
          signInLabel={strings.signInToSeePricing}
          colorChoice={colorChoice || undefined}
        />
      </div>
    </Link>
    <QuickAddPopover
      card={card}
      open={popoverOpen}
      onClose={() => setPopoverOpen(false)}
      strings={strings}
      soldOutKeys={soldOutKeys}
      onAdded={(msg) => pushAdd(msg, variety.slug)}
    />
    </>
  );
}
